import { useNavigate } from 'react-router-dom';
import { SponsoredCardData } from './types';
import { ClockIcon, PeopleIcon, ChecklistIcon, LikeIcon } from '../activity-details/icons';
import { createActivityNavigationHandler } from '../../utils/navigation';
import {
  CARD_DECK_COLORS,
  CARD_DECK_DIMENSIONS,
  CARD_DECK_RADIUS,
  CARD_DECK_SHADOWS,
  CARD_DECK_SPACING,
  CARD_DECK_TYPOGRAPHY,
} from './constants';

interface SponsoredDeckCardProps {
  card: SponsoredCardData;
}

/**
 * SponsoredDeckCard Component
 * 
 * Sponsored/partnership card for the card deck.
 * Displays sponsor badge, partner info, image, title,
 * metadata, description, and CTA button.
 * 
 * Design specs from Figma:
 * - Same dimensions as ActivityDeckCard
 * - Includes "Sponsored content" badge on image
 * - Partner logo and name section
 */
export default function SponsoredDeckCard({ card }: SponsoredDeckCardProps) {
  const navigate = useNavigate();
  const handleActivityClick = createActivityNavigationHandler(card.id, navigate);
  
  // Format like count (e.g., 1300 -> "1.3K")
  const formatLikeCount = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return count.toString();
  };

  return (
    <div
      className="flex flex-col bg-white overflow-hidden w-full aspect-[382/462]"
      style={{
        maxWidth: `${CARD_DECK_DIMENSIONS.frontCard.width}px`,
        borderRadius: `${CARD_DECK_RADIUS.card}px`,
        padding: `${CARD_DECK_SPACING.cardPadding}px`,
        boxShadow: CARD_DECK_SHADOWS.card,
        gap: `${CARD_DECK_SPACING.cardGap}px`,
      }}
    >
      {/* Sponsored Image with Badges */}
      <div
        className="relative overflow-hidden flex-shrink-0 aspect-[382/180]"
        style={{
          borderRadius: `${CARD_DECK_RADIUS.image}px`,
        }}
      >
        <img
          src={card.imageUrl}
          alt={card.title}
          className="w-full h-full object-cover"
        />
        
        {/* Sponsored Badge (top-left) */}
        <div
          className="absolute top-2 left-2 flex items-center"
          style={{
            backgroundColor: CARD_DECK_COLORS.sponsoredBadgeBg,
            borderRadius: `${CARD_DECK_RADIUS.sponsoredBadge}px`,
            padding: '4px 8px',
          }}
        >
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '11px',
              fontWeight: 500,
              color: CARD_DECK_COLORS.sponsoredBadgeText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.badgeText}
          </span>
        </div>

        {/* Like Count Badge (bottom-right) */}
        <div
          className="absolute bottom-2 right-2 flex items-center gap-1"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: `${CARD_DECK_RADIUS.likeBadge}px`,
            padding: '4px 8px',
            boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.25)',
          }}
        >
          <LikeIcon size={14} color="#FFFFFF" />
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: '#FFFFFF',
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {formatLikeCount(card.likesCount)} {card.likesLabel}
          </span>
        </div>
      </div>

      {/* Partner Section */}
      <div className="flex items-center gap-2">
        <img
          src={card.partner.logoUrl}
          alt={`${card.partner.name} logo`}
          className="object-cover"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '15px',
          }}
        />
        <div className="flex flex-col">
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            In partnership with
          </span>
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.partner.name}
          </span>
        </div>
      </div>

      {/* Title */}
      <h3
        style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          letterSpacing: '-0.36px',
          color: CARD_DECK_COLORS.titleText,
          margin: 0,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        {card.title}
      </h3>

      {/* Metadata Row */}
      <div
        className="flex items-center"
        style={{ gap: `${CARD_DECK_SPACING.metadataGap}px` }}
      >
        {/* Duration */}
        <div className="flex items-center gap-1">
          <ClockIcon size={14} color={CARD_DECK_COLORS.titleText} />
          <span
            style={{
              ...CARD_DECK_TYPOGRAPHY.metadata,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.duration}
          </span>
        </div>

        {/* Play Type */}
        <div className="flex items-center gap-1">
          <PeopleIcon size={14} color={CARD_DECK_COLORS.titleText} />
          <span
            style={{
              ...CARD_DECK_TYPOGRAPHY.metadata,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.playType}
          </span>
        </div>

        {/* Prep Status */}
        <div className="flex items-center gap-1 flex-1">
          <ChecklistIcon size={14} color={CARD_DECK_COLORS.titleText} />
          <span
            style={{
              ...CARD_DECK_TYPOGRAPHY.metadata,
              color: CARD_DECK_COLORS.titleText,
              fontVariationSettings: "'opsz' 14",
            }}
          >
            {card.metadata.prepNeeded}
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        className="flex-1"
        style={{
          ...CARD_DECK_TYPOGRAPHY.description,
          color: CARD_DECK_COLORS.descriptionText,
          margin: 0,
          fontVariationSettings: "'opsz' 14",
          lineHeight: 1.3,
        }}
      >
        {card.description}
      </p>

      {/* CTA Button */}
      <button
        onClick={handleActivityClick}
        className="w-full flex items-center justify-center"
        style={{
          height: `${CARD_DECK_DIMENSIONS.buttonHeight}px`,
          backgroundColor: CARD_DECK_COLORS.buttonBackground,
          borderRadius: `${CARD_DECK_RADIUS.button}px`,
          border: 'none',
          cursor: 'pointer',
          ...CARD_DECK_TYPOGRAPHY.button,
          color: CARD_DECK_COLORS.buttonText,
          fontVariationSettings: "'opsz' 14",
        }}
      >
        TRY THIS SPARK
      </button>
    </div>
  );
}
